import React, { useEffect, useMemo } from 'react';

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: 'weapon' | 'armor' | 'accessory' | 'consumable' | 'material';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  equipSlot?: string;
  stats?: Record<string, number>;
  buyPrice: number;
  sellPrice: number;
  icon: string;
}

interface HarvestDrop {
  itemId: string;
  quantity: number;
  item?: ItemDefinition | null;
}

interface HarvestRewardToastProps {
  isOpen: boolean;
  drops: HarvestDrop[];
  xpGained?: number;
  bonusApplied?: string;
  skillType?: string;
  onClose: () => void;
}

const renderItemIcon = (icon: string | undefined, name: string): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  const resolvedSrc = icon && icon.startsWith('/assets')
    ? `${publicUrl}${icon}`
    : icon && (icon.startsWith('http://') || icon.startsWith('https://'))
    ? icon
    : `${publicUrl}/assets/unknown.png`;

  return (
    <img
      src={resolvedSrc}
      alt={name}
      style={{ width: 28, height: 28, objectFit: 'contain', imageRendering: 'pixelated' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        if (target.src !== `${publicUrl}/assets/unknown.png`) {
          target.onerror = null;
          target.src = `${publicUrl}/assets/unknown.png`;
        }
      }}
    />
  );
};

const getContextualTitle = (skillType?: string): string => {
  switch (skillType?.toLowerCase()) {
    case 'mining':
      return 'MINED';
    case 'foraging':
      return 'FORAGED';
    case 'fishing':
      return 'CAUGHT';
    case 'salvaging':
      return 'SALVAGED';
    default:
      return 'GATHERED';
  }
};

const formatItemName = (itemId: string): string => {
  return itemId
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const HarvestRewardToast: React.FC<HarvestRewardToastProps> = ({
  isOpen,
  drops,
  xpGained,
  bonusApplied,
  skillType,
  onClose,
}): React.ReactElement | null => {
  const hasXp: boolean = typeof xpGained === 'number' && xpGained > 0;

  const normalizedDrops = useMemo(() => {
    return drops
      .filter((d) => d && d.quantity > 0)
      .map((d) => ({
        ...d,
        displayName: d.item?.name || formatItemName(d.itemId),
      }));
  }, [drops]);

  useEffect(() => {
    if (!isOpen) return;
    const timeout = window.setTimeout(() => onClose(), 3000);
    return () => window.clearTimeout(timeout);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="font-mono"
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 90,
        transform: 'translateX(-50%)',
        width: 360,
        background: 'rgba(13, 17, 23, 0.96)',
        border: '2px solid #30363d',
        zIndex: 450,
      }}
      onClick={onClose}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #21262d',
          background: '#161b22',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ color: '#22c55e', fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>{getContextualTitle(skillType)}</div>
        {bonusApplied && (
          <div style={{ color: '#fbbf24', fontSize: 11 }}>{bonusApplied}</div>
        )}
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {normalizedDrops.map((drop) => (
          <div key={drop.itemId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                background: '#0d1117',
                border: '1px solid #30363d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderItemIcon(drop.item?.icon, drop.displayName)}
            </div>
            <div style={{ flex: 1, color: '#e5e7eb', fontSize: 12 }}>
              {drop.displayName}
            </div>
            <div style={{ color: '#9ca3af', fontSize: 12 }}>x{drop.quantity}</div>
          </div>
        ))}

        {hasXp && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                background: '#0d1117',
                border: '1px solid #fbbf24',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fbbf24',
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              XP
            </div>
            <div style={{ flex: 1, color: '#fbbf24', fontSize: 12 }}>Experience</div>
            <div style={{ color: '#fbbf24', fontSize: 12 }}>+{xpGained}</div>
          </div>
        )}
      </div>
    </div>
  );
};
